import { useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import NumberInput from '../../components/NumberInput';

import ResourceCounter from '../../components/options/ResourceCounter';
import IconStar from '../../components/options/IconStar';
import './HitPoints.css';

export type Health = {
    currentHp: number; tempHp: number; maxHp: number;
    spentHitDice: number; maxHitDice: number;
    deathSaveSuccesses: number; deathSaveFailures: number;
};

export const emptyHealth: Health = {
    currentHp: 0, tempHp: 0, maxHp: 0, spentHitDice: 0, maxHitDice: 0,
    deathSaveSuccesses: 0, deathSaveFailures: 0,
};

function HitPoints({ characterId, value, onChange }: { characterId: number; value: Health; onChange: (health: Health) => void }) {
    const { apiRequest } = useAuth();
    const [error, setError] = useState('');
    const [pending, setPending] = useState(0);
    const queue = useRef<Promise<void>>(Promise.resolve());
    const save = (health: Health) => {
        onChange(health);
        setPending(count => count + 1);
        queue.current = queue.current.then(async () => {
            try {
                await apiRequest(`/api/characters/${characterId}/health`, 'PATCH', health);
                setError('');
            } catch {
                setError('Could not save health. Try again.');
            } finally {
                setPending(count => count - 1);
            }
        });
    };
    const update = (field: keyof Health) => (next: number) => {
        if (!Number.isInteger(next) || next < 0 || next > 2147483647) {
            setError('Enter a non-negative integer.');
            return;
        }
        save({ ...value, [field]: next });
    };
    const { currentHp, tempHp, maxHp, spentHitDice, maxHitDice, deathSaveSuccesses, deathSaveFailures } = value;
    const barTotal = maxHp + tempHp;
    const hpWidth = barTotal > 0 ? Math.max(0, Math.min(currentHp, maxHp)) / barTotal * 100 : 0;
    const tempWidth = barTotal > 0 ? tempHp / barTotal * 100 : 0;
    const setCurrentHp = update('currentHp');
    const setTempHp = update('tempHp');
    const setMaxHp = update('maxHp');
    const setSpentHitDice = update('spentHitDice');
    const setMaxHitDice = update('maxHitDice');
    const setDeathSaveSuccesses = update('deathSaveSuccesses');
    const setDeathSaveFailures = update('deathSaveFailures');

    const numberInput = (
        label: string,
        value: number,
        onChange: (value: number) => void,
        max?: number,
    ) => (
        <label className="hit-points__number-field">
            <NumberInput
                aria-label={`${label} hit points`}
                min={0}
                max={max}
                value={value}
                onValueChange={onChange}
                className="hit-points__input"
            />
            <span>{label}</span>
        </label>
    );

    return (
        <section className="hit-points" aria-label="Health and survival">
            <small className="visually-hidden" role="status">{pending > 0 ? 'Saving health…' : ''}</small>
            {error && <small role="alert">{error} <button type="button" disabled={pending > 0} onClick={() => save(value)}>Retry</button></small>}
            <div className="hit-points__group">
                <h2><i className="fa-solid fa-heart" aria-hidden="true" /> Hit Points</h2>
                <div className="hit-points__numbers hit-points__numbers--hp">
                    <div className="hit-points__total">
                        {numberInput('Current', currentHp, setCurrentHp, maxHp)}
                        <span className="hit-points__slash" aria-hidden="true">/</span>
                        {numberInput('Max', maxHp, setMaxHp)}
                    </div>
                    <div className="hit-points__bar" aria-hidden="true">
                        <span style={{ width: `${hpWidth}%` }} />
                        <span className="hit-points__bar-temp" style={{ width: `${tempWidth}%` }} />
                    </div>
                    {numberInput('Temp', tempHp, setTempHp)}
                </div>
            </div>

            <details className="health-details">
                <summary>Hit dice & death saves</summary>
            <div className="hit-points__group">
                <h2>Hit Dice</h2>
                <div className="hit-points__numbers hit-points__numbers--two">
                    {numberInput('Spent', spentHitDice, setSpentHitDice, maxHitDice)}
                    {numberInput('Max', maxHitDice, setMaxHitDice)}
                </div>
            </div>

            <div className="hit-points__group hit-points__death-saves">
                <h2>Death Saves</h2>
                <ResourceCounter name="Success" total={3} spent={deathSaveSuccesses} onChange={setDeathSaveSuccesses} Icon={IconStar} size="2rem" />
                <ResourceCounter name="Failure" total={3} spent={deathSaveFailures} onChange={setDeathSaveFailures} Icon={IconStar} size="2rem" />
            </div>
            </details>
        </section>
    );
}

export default HitPoints;
